import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Lan as ConnectIcon,
  LockClock as HoldIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  ParkingQaCctv,
  ParkingQaStatus,
  QaLive,
  QaMapLevel,
  QaModuleReport,
  QaPairingReport,
  QaPingCctv,
  QaSpace,
  QaState,
  SiteParkingQaService,
} from '../../../services/SiteParkingQaService';
import QaSiteMap, {
  BELL_COLOR,
  INSPECT_COLOR,
  LIGHT_COLOR,
  MISMATCH_COLOR,
  NEUTRAL_COLOR,
  SPACE_COLOR,
} from '../../../components/qa/QaSiteMap';
import QaScenarioPanel from '../../../components/qa/QaScenarioPanel';
import QaCctvDialog from '../../../components/qa/QaCctvDialog';
import QaAislePanel from '../../../components/qa/QaAislePanel';
import QaLightControlPanel from '../../../components/qa/QaLightControlPanel';
import QaPingPanel from '../../../components/qa/QaPingPanel';
import QaModulePanel from '../../../components/qa/QaModulePanel';
import QaPairingPanel from '../../../components/qa/QaPairingPanel';
import QaOccupancyLightPanel from '../../../components/qa/QaOccupancyLightPanel';
import { EMPTY_AISLE_WATCH, foldAisle } from '../../../components/qa/qaAisle';
import {
  moduleMarks,
  pairingMarks,
  pingMarks,
  type QaInspectMarks,
} from '../../../components/qa/qaInspect';
import { SiteNetworkDeviceService } from '../../../services/SiteNetworkDeviceService';
import { CATEGORY_INTEGRATION_SERVER } from '../../SiteSettingsPage/HealthTargetCard';

interface ParkingQaSectionProps {
  projectId: number;
}

/** 카메라 반영 중지 시간. router 가 60~7200초만 받는다. */
const HOLD_OPTIONS = [
  { seconds: 60, label: '1분' },
  { seconds: 300, label: '5분' },
  { seconds: 600, label: '10분' },
  { seconds: 1800, label: '30분' },
  { seconds: 3600, label: '1시간' },
  { seconds: 7200, label: '2시간' },
];
const NO_LEVEL = '층 미지정';

/** 테스트 4가지 — 탭마다 그 테스트에 필요한 맵 표시와 버튼만 보인다. */
type QaTab = 'aisle' | 'light' | 'board' | 'entrance' | 'ping' | 'module' | 'pairing';
const TABS: { value: QaTab; label: string }[] = [
  { value: 'aisle', label: '통로등' },
  { value: 'light', label: '만공차등' },
  { value: 'board', label: '단면 전광판' },
  { value: 'entrance', label: '입구 알림판' },
  { value: 'ping', label: 'CCTV 핑' },
  { value: 'module', label: '모듈 통신' },
  { value: 'pairing', label: '페어링' },
];
/**
 * 자동 갱신 주기 — 통합서버에 SSH 한 번(약 1초)씩이라 너무 짧게 잡지 않는다.
 * 통로등은 지나가는 동안의 변화를 잡아야 해서 짧게, 만공차등은 조명 반응을 보려고 조금 짧게.
 */
const LIVE_MS: Record<QaTab, number> = {
  aisle: 2_000,
  light: 5_000,
  board: 10_000,
  entrance: 10_000,
  ping: 10_000,
  module: 10_000,
  pairing: 10_000,
};
/** router 가 카메라 판정을 끊김으로 보는 기준(parking.service MAX_STALENESS_SECONDS). */
const CAMERA_STALE_SECONDS = 30;

const levelOf = (c: ParkingQaCctv) => c.levelName || NO_LEVEL;
const NO_LIGHTS = '조명 서비스에 이 층 조명이 등록돼 있지 않습니다';
const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
/** 맵 CCTV 글자(이름·IP) 표시 여부 — 이 브라우저에 기억한다. 기본은 둘 다 보인다. */
const LABEL_KEY = 'qa-map-labels';
const readLabels = (): { name: boolean; ip: boolean } => {
  try {
    const v = JSON.parse(localStorage.getItem(LABEL_KEY) || '{}');
    return { name: v.name !== false, ip: v.ip !== false };
  } catch {
    return { name: true, ip: true };
  }
};

/** 현장마다 마지막으로 연결한 통합서버 IP 를 이 브라우저에 기억한다. */
const ipKey = (projectId: number) => `qa-server-ip:${projectId}`;
const readSavedIp = (projectId: number) => {
  try {
    return localStorage.getItem(ipKey(projectId)) || '';
  } catch {
    return '';
  }
};

/**
 * QA 주차 테스트 — 현장 통합서버에 연결해 통로등·만공차등·단면 유도 전광판·입구 종합 알림판을
 * 탭 하나씩 따로 확인한다. 먼저 통합서버 IP 를 넣고 연결한다(계정은 luxrobo 고정 — 백엔드). 연결되면
 * 통합서버 DB 의 맵을 층별로 그리고, 주차면 조회·변경도 그 IP 로 보낸다.
 * 입구현황판·전광판은 DB 점유 수를, 만공차등은 CCTV 별 만차/공차 판정을 따른다. 바꾼 CCTV 는 고정
 * 시간 동안 카메라 값이 DB 를 덮어쓰지 않는다.
 */
const ParkingQaSection: React.FC<ParkingQaSectionProps> = ({ projectId }) => {
  const [ipInput, setIpInput] = useState(() => readSavedIp(projectId));
  const [ip, setIp] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [mapLevels, setMapLevels] = useState<QaMapLevel[]>([]);
  const [cctvs, setCctvs] = useState<ParkingQaCctv[] | null>(null);
  // 현장 DB 의 모든 주차면(종류·CCTV 미연결 포함)과 단면 유도 전광판 — 맵·시나리오가 쓴다.
  const [qaState, setQaState] = useState<QaState | null>(null);
  const [openCctv, setOpenCctv] = useState<string | null>(null);
  // 자동 갱신 — 주차 점유·QA 고정·카메라 판정·조명 디밍을 LIVE_MS 마다 다시 읽는다.
  const [live, setLive] = useState<(QaLive & { at: number }) | null>(null);
  const [liveOn, setLiveOn] = useState(true);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [, setTick] = useState(0);
  // 주차면 id → 마지막으로 바꾼 시각. 그보다 먼저 시작한 갱신 결과는 그 면을 덮어쓰지 않는다.
  const changedAt = useRef(new Map<number, number>());
  const liveInflight = useRef(false);
  const holdRevision = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<{ severity: AlertColor; text: string } | null>(null);
  const [level, setLevel] = useState('');
  const [query, setQuery] = useState('');
  const [holdSeconds, setHoldSeconds] = useState(600);
  const [tab, setTab] = useState<QaTab>('aisle');
  const [labels, setLabels] = useState(readLabels);
  const toggleLabel = (key: 'name' | 'ip', on: boolean) =>
    setLabels((prev) => {
      const next = { ...prev, [key]: on };
      try {
        localStorage.setItem(LABEL_KEY, JSON.stringify(next));
      } catch {
        // 기억 못 해도 지금 화면에는 반영된다.
      }
      return next;
    });
  // 통로등 테스트 — 갱신마다 CCTV 별 통로등 밝기를 모은다. 층·서버가 바뀌면 처음부터.
  const [aisle, setAisle] = useState(EMPTY_AISLE_WATCH);
  // 점검 탭 결과 — 맵에 문제 있는 것만 칠한다.
  const [pingResult, setPingResult] = useState<QaPingCctv[] | null>(null);
  const [moduleResult, setModuleResult] = useState<QaModuleReport | null>(null);
  const [pairingResult, setPairingResult] = useState<QaPairingReport | null>(null);

  const load = useCallback(async () => {
    if (!ip) return;
    setError(null);
    const [router, db] = await Promise.allSettled([
      SiteParkingQaService.cctvs(projectId, ip),
      SiteParkingQaService.state(projectId, ip),
    ]);
    if (router.status === 'fulfilled') setCctvs(router.value);
    if (db.status === 'fulfilled') setQaState(db.value);
    const failed = [router, db].filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failed.length) setError(failed.map((r) => errorText(r.reason, '조회 실패')).join(' / '));
  }, [projectId, ip]);

  useEffect(() => {
    load();
  }, [load]);

  const mapAreaRef = useRef<HTMLDivElement | null>(null);
  // 연결되면 맵이 보이게 내려 준다 — 휴대폰에서는 위 입력칸이 화면을 거의 채운다.
  useEffect(() => {
    if (ip) mapAreaRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [ip]);

  // 현장설정(원격 상태 점검 접속 정보)의 통합서버 IP 를 미리 채운다 — 현장마다 IP 가 달라
  // 매번 찾아 적지 않게. 이 브라우저에서 마지막으로 연결한 IP 가 있으면 그쪽을 둔다.
  useEffect(() => {
    if (readSavedIp(projectId)) return;
    let alive = true;
    SiteNetworkDeviceService.list(projectId)
      .then((devices) => {
        const server = devices.find(
          (d) => d.category === CATEGORY_INTEGRATION_SERVER && d.ip_address,
        );
        if (alive && server) setIpInput((cur) => cur || server.ip_address.trim());
      })
      .catch(() => {
        // 현장설정이 없거나 못 읽으면 손으로 입력한다.
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  const connect = async () => {
    const target = ipInput.trim();
    if (!IPV4.test(target)) {
      setError('통합서버 IP 를 확인해 주세요 (예: 172.23.190.42)');
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      setMapLevels(await SiteParkingQaService.map(projectId, target));
      setCctvs(null);
      setQaState(null);
      setIp(target);
      try {
        localStorage.setItem(ipKey(projectId), target);
      } catch {
        // 저장 못 해도 연결은 된다 — 다음에 다시 입력하면 된다.
      }
    } catch (e) {
      setError(errorText(e, '통합서버 연결 실패'));
    } finally {
      setConnecting(false);
    }
  };

  // 층 목록 — 맵 층과 router CCTV 층을 합친다.
  const levels = useMemo(
    () =>
      Array.from(new Set([...mapLevels.map((l) => l.name), ...(cctvs ?? []).map(levelOf)])).sort(
        (a, b) => a.localeCompare(b, 'ko'),
      ),
    [mapLevels, cctvs],
  );
  const activeLevel = levels.includes(level) ? level : levels[0] ?? '';
  const heldCount = (cctvs ?? []).filter((c) => c.held).length;
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (cctvs ?? []).filter(
      (c) =>
        levelOf(c) === activeLevel &&
        (!q ||
          c.name.toLowerCase().includes(q) ||
          c.spaces.some((s) => s.name.toLowerCase().includes(q))),
    );
  }, [cctvs, activeLevel, query]);

  /** 만공차등 판정 결과를 사람이 읽는 문장으로. */
  const verdictText = (verdicts: { cctvName: string; full: boolean }[]) =>
    verdicts.length
      ? `만공차등 판정 발행: ${verdicts
          .map((v) => `${v.cctvName} ${v.full ? '만차(소등)' : '공차(점등)'}`)
          .join(', ')}`
      : 'CCTV 에 연결되지 않은 면이라 만공차등 판정은 보내지 않았습니다.';

  /** 주차면 점유를 바꾼다. quiet 면 결과 알림을 띄우지 않는다(시나리오가 모아서 띄운다). */
  const markChanged = (ids: number[]) => {
    const now = Date.now();
    ids.forEach((id) => changedAt.current.set(id, now));
  };

  const apply = async (ids: number[], status: ParkingQaStatus, quiet = false): Promise<boolean> => {
    if (busy) return false;
    if (!ids.length) return true;
    markChanged(ids);
    setPending((prev) => new Set([...prev, ...ids]));
    try {
      const verdicts = await SiteParkingQaService.setStatus(
        projectId,
        ids,
        status,
        holdSeconds,
        ip ?? undefined,
      );
      const held = new Set(verdicts.map((v) => v.cctvName));
      const changed = new Set(ids);
      setCctvs((prev) =>
        (prev ?? []).map((c) => ({
          ...c,
          held: c.held || held.has(c.name),
          spaces: c.spaces.map((s) => (changed.has(s.id) ? { ...s, status } : s)),
        })),
      );
      setQaState(
        (prev) =>
          prev && {
            ...prev,
            spaces: prev.spaces.map((s) => (changed.has(s.id) ? { ...s, status } : s)),
          },
      );
      setLive((prev) => prev && { ...prev, held: Array.from(new Set([...prev.held, ...held])) });
      if (!quiet) setToast({ severity: 'info', text: verdictText(verdicts) });
      return true;
    } catch (e) {
      setToast({ severity: 'error', text: errorText(e, '주차면 상태 변경 실패') });
      return false;
    } finally {
      markChanged(ids);
      setPending((prev) => new Set([...prev].filter((id) => !ids.includes(id))));
    }
  };

  const hold = async () => {
    holdRevision.current += 1;
    setBusy(true);
    try {
      const held = new Set(
        await SiteParkingQaService.hold(projectId, holdSeconds, ip ?? undefined),
      );
      setCctvs((prev) => prev && prev.map((c) => ({ ...c, held: held.has(c.name) || c.held })));
      setLive((prev) => prev && { ...prev, held: Array.from(new Set([...prev.held, ...held])) });
      setToast({
        severity: 'success',
        text: `전체 CCTV ${held.size}대 영상처리서버 반영을 ${holdSeconds / 60}분간 중지했습니다.`,
      });
    } catch (e) {
      setToast({ severity: 'error', text: errorText(e, '카메라 반영 중지 실패') });
    } finally {
      holdRevision.current += 1;
      setBusy(false);
    }
  };

  const release = async () => {
    holdRevision.current += 1;
    setBusy(true);
    try {
      const released = await SiteParkingQaService.release(projectId, ip ?? undefined);
      setCctvs((prev) => (prev ?? []).map((c) => ({ ...c, held: false })));
      setLive((prev) => prev && { ...prev, held: [] });
      setToast({
        severity: 'success',
        text: `QA 고정 ${released.length}대 해제 — 카메라 반영이 돌아오고 해당 만공차등은 점등으로 돌렸습니다.`,
      });
    } catch (e) {
      setToast({ severity: 'error', text: errorText(e, 'QA 고정 해제 실패') });
    } finally {
      holdRevision.current += 1;
      setBusy(false);
    }
  };

  /** 여유/혼잡/만차 시나리오 — 점유·빈자리 두 번 보내고 결과를 한 번에 알린다. */
  const applyPlan = async (plan: { occupy: number[]; vacate: number[] }, label: string) => {
    const ok = (await apply(plan.occupy, 'Y', true)) && (await apply(plan.vacate, 'N', true));
    if (ok) {
      setToast({
        severity: 'success',
        text: `${label}(으)로 맞췄습니다 — 점유 ${plan.occupy.length}면 · 빈자리 ${plan.vacate.length}면`,
      });
    }
  };

  const heldCctvs = useMemo(
    () =>
      live ? new Set(live.held) : new Set((cctvs ?? []).filter((c) => c.held).map((c) => c.name)),
    [live, cctvs],
  );
  const activeMapLevel = mapLevels.find((l) => l.name === activeLevel);
  const activeMap = activeMapLevel?.mapData;
  // 조명 위치(P1_B5) — 조명 서비스는 CCTV 이름 앞 두 토큰으로 묶는다. 맵 CCTV 이름에서 꺼내고,
  // 없으면 주차장 이름 + 층.
  const lightLocation = useMemo(() => {
    const md = activeMap as { cctvs?: { lightCctvs?: { name?: string }[] } } | undefined;
    const name = (md?.cctvs?.lightCctvs ?? [])
      .map((c) => c?.name)
      .find((n) => !!n && n.split('_').length >= 2);
    if (name) return name.split('_').slice(0, 2).join('_');
    return activeMapLevel ? `${activeMapLevel.lot || 'P1'}_${activeMapLevel.name}` : '';
  }, [activeMap, activeMapLevel]);

  const stateReady = qaState !== null;
  const liveMs = LIVE_MS[tab];

  /** 갱신 결과를 화면 상태에 합친다. started 뒤에 바꾼 면은 건드리지 않는다. */
  const applyLive = useCallback((l: QaLive, started: number) => {
    const status = new Map<number, ParkingQaStatus>();
    l.spaces.forEach(([id, st]) => {
      if ((changedAt.current.get(id) ?? 0) < started) status.set(id, st);
    });
    const held = new Set(l.held);
    setQaState(
      (prev) =>
        prev && {
          ...prev,
          spaces: prev.spaces.map((sp) => {
            const st = status.get(sp.id);
            return st && st !== sp.status ? { ...sp, status: st } : sp;
          }),
        },
    );
    setCctvs((prev) =>
      prev
        ? prev.map((c) => ({
            ...c,
            held: held.has(c.name),
            spaces: c.spaces.map((sp) => {
              const st = status.get(sp.id);
              return st && st !== sp.status ? { ...sp, status: st } : sp;
            }),
          }))
        : prev,
    );
    // 통로등 수동 중엔 값이 모드대로 고정이라 감지 반응 점검에서 뺀다 (켬·끔 전환이 "반응" 으로 잡히지 않게).
    if (!l.lightsError && l.lightMode?.aisle.mode !== 'manual')
      setAisle((prev) => foldAisle(prev, l.lights, Date.now()));
    setLive({ ...l, at: Date.now() });
  }, []);

  // 자동 갱신 루프 — 한 줄로만 돈다(앞 요청이 끝나야 다음 예약). 화면이 꺼져 있으면 건너뛰고,
  // 다시 켜지면 바로 한 번 읽는다.
  useEffect(() => {
    // 첫 DB 상태를 읽은 뒤에 돈다 — 먼저 돌면 그 결과가 늦게 온 첫 상태에 덮인다.
    if (!ip || !liveOn || !stateReady || !activeLevel || !lightLocation) return undefined;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const run = async () => {
      timer = undefined;
      if (stop) return;
      if (document.visibilityState !== 'hidden') {
        liveInflight.current = true;
        const started = Date.now();
        const revision = holdRevision.current;
        try {
          const l = await SiteParkingQaService.live(projectId, ip, activeLevel, lightLocation);
          if (!stop && revision === holdRevision.current) {
            applyLive(l, started);
            setLiveError(null);
          }
        } catch (e) {
          if (!stop) setLiveError(errorText(e, '자동 갱신 실패'));
        } finally {
          liveInflight.current = false;
        }
      }
      if (!stop) timer = setTimeout(run, liveMs);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !liveInflight.current && timer) {
        clearTimeout(timer);
        run();
      }
    };
    run();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ip, liveOn, stateReady, activeLevel, lightLocation, projectId, applyLive, liveMs]);

  useEffect(() => {
    setAisle(EMPTY_AISLE_WATCH);
  }, [ip, activeLevel]);

  // "n초 전 갱신" 글자를 위해 1초마다 다시 그린다.
  useEffect(() => {
    if (!live || !liveOn) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [live, liveOn]);

  // 카메라 판정 — 30초 안의 판정만 믿는다(router 와 같은 기준).
  const { cameraBySpace, cameraHealth } = useMemo(() => {
    const bySpace = new Map<string, boolean>();
    const health = new Map<string, 'ok' | 'stale' | 'none'>();
    if (!live) return { cameraBySpace: undefined, cameraHealth: undefined };
    const md = activeMap as { cctvs?: { lightCctvs?: { name?: string }[] } } | undefined;
    (md?.cctvs?.lightCctvs ?? []).forEach((c) => {
      if (!c?.name) return;
      const d = live.detection[c.name];
      if (!d) health.set(c.name, 'none');
      else if (live.now - d.timestamp > CAMERA_STALE_SECONDS) health.set(c.name, 'stale');
      else {
        health.set(c.name, 'ok');
        d.parking.forEach((p) => bySpace.set(p.id, p.status));
      }
    });
    return { cameraBySpace: bySpace, cameraHealth: health };
  }, [live, activeMap]);

  // 맵 주차면 이름 → DB 주차면 (이 층만 — 층마다 parkingLocations-1 처럼 이름이 겹친다).
  // 층은 연결 CCTV 로 정해지므로, CCTV 미연결 면은 이 층 맵에 같은 이름이 있을 때만 붙인다.
  // DB 상태를 아직 못 읽었으면 router CCTV 목록으로 대신한다.
  const levelSpaces = useMemo(() => {
    const m = new Map<string, { id: number; occupied: boolean; fixed: boolean; held: boolean }>();
    if (qaState) {
      const onMap = new Set(
        (
          (activeMap as { parkingLocations?: { name?: string }[] } | undefined)?.parkingLocations ??
          []
        )
          .map((p) => p?.name)
          .filter((n): n is string => !!n),
      );
      const put = (s: QaSpace) =>
        m.set(s.name, {
          id: s.id,
          occupied: s.status === 'Y',
          fixed: s.fixed,
          held: !!s.cctv && heldCctvs.has(s.cctv),
        });
      qaState.spaces.filter((s) => s.level === activeLevel).forEach(put);
      qaState.spaces.filter((s) => !s.level && onMap.has(s.name) && !m.has(s.name)).forEach(put);
      return m;
    }
    (cctvs ?? [])
      .filter((c) => levelOf(c) === activeLevel)
      .forEach((c) =>
        c.spaces.forEach((s) =>
          m.set(s.name, { id: s.id, occupied: s.status === 'Y', fixed: s.isFixed, held: c.held }),
        ),
      );
    return m;
  }, [qaState, cctvs, activeLevel, activeMap, heldCctvs]);
  const levelIds = useMemo(() => [...levelSpaces.values()].map((s) => s.id), [levelSpaces]);
  const mismatchCount = useMemo(
    () =>
      cameraBySpace
        ? [...levelSpaces.entries()].filter(([name, s]) => {
            const seen = cameraBySpace.get(name);
            return seen !== undefined && seen !== s.occupied && !s.held;
          }).length
        : 0,
    [cameraBySpace, levelSpaces],
  );
  // 이 층 조명 요약 — 맵을 다 보이게 줄이면 % 글자가 안 보여서 한 줄로 따로 보여 준다.
  const lightSummary = useMemo(() => {
    if (!live || live.lightsError) return null;
    const aisle = new Map<number, number>();
    let parkOn = 0;
    let parkOff = 0;
    Object.values(live.lights).forEach((l) =>
      l.nodes.forEach((n) => {
        if (n.kind === 'aisle') aisle.set(n.value, (aisle.get(n.value) ?? 0) + 1);
        else if (n.value > 0) parkOn += 1;
        else parkOff += 1;
      }),
    );
    if (!aisle.size && !parkOn && !parkOff) return null;
    const aisleText = [...aisle.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([v, n]) => `${v > 0 ? `${v}%` : '꺼짐'} ${n}개`)
      .join(' · ');
    return { aisleText, parkOn, parkOff };
  }, [live]);
  const deadCount = cameraHealth ? [...cameraHealth.values()].filter((h) => h !== 'ok').length : 0;
  const liveAge = live ? Math.max(0, Math.round((Date.now() - live.at) / 1000)) : null;
  const occupiedCount = useMemo(
    () => [...levelSpaces.values()].filter((s) => s.occupied).length,
    [levelSpaces],
  );
  const pendingNames = useMemo(
    () =>
      new Set(
        [...levelSpaces.entries()].filter(([, s]) => pending.has(s.id)).map(([name]) => name),
      ),
    [levelSpaces, pending],
  );
  // 점검 탭(핑·모듈·페어링)은 문제 있는 것만 칠한다 — 점유·조명·카메라 판정 색은 모두 뺀다.
  const inspect = useMemo<QaInspectMarks | undefined>(() => {
    if (tab === 'ping') return pingMarks(pingResult ?? []);
    if (tab === 'module') return moduleMarks(moduleResult ?? { cctvs: [] });
    if (tab === 'pairing') return pairingMarks(pairingResult ?? { bells: [], noAisle: [], dupMac: [] });
    return undefined;
  }, [tab, pingResult, moduleResult, pairingResult]);
  // 통로등 탭은 점유를 바꾸지 않는다 — 맵에 주차면 점유색도 그리지 않는다.
  const showSpaces = tab !== 'aisle' && !inspect;
  const aisleReacted = useMemo(
    () =>
      new Set(
        Object.entries(aisle.cctvs)
          .filter(([, c]) => c.reactedAt !== undefined)
          .map(([n]) => n),
      ),
    [aisle],
  );
  const tapSpace = (name: string) => {
    const s = levelSpaces.get(name);
    if (!s || pending.has(s.id)) return;
    apply([s.id], s.occupied ? 'N' : 'Y');
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          QA 주차 테스트
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          통합서버에 연결한 뒤 아래 탭에서 테스트할 항목(통로등·만공차등·단면 전광판·입구 알림판)을
          고르세요. 점유를 바꾼 CCTV 는 카메라 값이 반영되지 않으니 끝나면 고정을 해제하세요.
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2 }}
          alignItems="center"
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            connect();
          }}
        >
          <TextField
            size="small"
            label="통합서버 IP"
            placeholder="172.23.190.42"
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            sx={{ flex: { xs: 1, sm: '0 0 240px' }, minWidth: 0 }}
            inputProps={{ 'data-testid': 'qa-server-ip', inputMode: 'decimal' }}
          />
          <Button
            type="submit"
            variant="contained"
            disableElevation
            startIcon={
              connecting ? <CircularProgress size={16} color="inherit" /> : <ConnectIcon />
            }
            disabled={connecting || !ipInput.trim()}
            data-testid="qa-server-connect"
            sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, minHeight: 40 }}
          >
            {connecting ? '연결 중…' : ip ? '다시 연결' : '연결'}
          </Button>
        </Stack>
        {ip && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`${ip} 연결됨 · 맵 ${mapLevels.length}개 층`}
            data-testid="qa-server-connected"
            sx={{ mt: 1 }}
          />
        )}
        {ip && (
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { xs: '1fr 1fr', md: '140px 150px auto 1fr auto auto' },
              alignItems: 'center',
            }}
          >
            <TextField
              select
              size="small"
              label="층"
              value={activeLevel}
              onChange={(e) => setLevel(e.target.value)}
              disabled={!levels.length}
            >
              {levels.map((l) => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="카메라 반영 중지"
              value={holdSeconds}
              onChange={(e) => setHoldSeconds(Number(e.target.value))}
              disabled={busy || pending.size > 0}
            >
              {HOLD_OPTIONS.map((o) => (
                <MenuItem key={o.seconds} value={o.seconds}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={hold}
              disabled={busy || pending.size > 0 || !cctvs?.length}
              sx={{ minHeight: 40 }}
            >
              적용
            </Button>
            <Box sx={{ display: { xs: 'none', md: 'block' } }} />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              disabled={busy}
              sx={{ minHeight: 40 }}
            >
              새로고침
            </Button>
            <Button
              variant="contained"
              color="warning"
              disableElevation
              startIcon={<HoldIcon />}
              onClick={release}
              disabled={busy || pending.size > 0 || heldCount === 0}
              sx={{ minHeight: 40, whiteSpace: 'nowrap' }}
            >
              {`QA 고정 전체 해제 (${heldCount})`}
            </Button>
          </Box>
        )}
        {ip && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            시간을 선택하고 적용하면 전체 CCTV의 영상처리서버 반영이 중지됩니다. 선택 시간이 지나면
            자동 해제되며, QA 고정 전체 해제로 즉시 해제할 수 있습니다.
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      {ip && (
        <Tabs
          value={tab}
          onChange={(_, v: QaTab) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="QA 테스트 종류"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, px: 0.5, fontWeight: 700, minWidth: 0 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} data-testid={`qa-tab-${t.value}`} />
          ))}
        </Tabs>
      )}

      {ip && (
        <Box ref={mapAreaRef} sx={{ scrollMarginBottom: 8 }}>
          {showSpaces && (
            <Box
              sx={{
                mb: 1,
                display: 'grid',
                gap: 1,
                alignItems: 'center',
                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr auto auto' },
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, flexWrap: 'wrap', rowGap: 0.5 }}
              >
                <Legend color={SPACE_COLOR.occupied} label={`점유 ${occupiedCount}`} />
                <Legend
                  color={SPACE_COLOR.empty}
                  label={`빈자리 ${levelSpaces.size - occupiedCount}`}
                />
                <Legend color="#64748b" label="CCTV 미연결" faded />
                {live && mismatchCount > 0 && (
                  <Legend color={MISMATCH_COLOR} label={`카메라와 다름 ${mismatchCount}`} dashed />
                )}
                {live && deadCount > 0 && (
                  <Legend color="#ef4444" label={`카메라 판정 끊김 ${deadCount}`} round />
                )}
              </Stack>
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={!levelIds.length || pending.size > 0}
                onClick={() => apply(levelIds, 'Y')}
                data-testid="qa-level-occupy"
                sx={{ minHeight: 36 }}
              >
                이 층 전체 점유
              </Button>
              <Button
                size="small"
                color="success"
                variant="outlined"
                disabled={!levelIds.length || pending.size > 0}
                onClick={() => apply(levelIds, 'N')}
                data-testid="qa-level-empty"
                sx={{ minHeight: 36 }}
              >
                이 층 전체 비움
              </Button>
            </Box>
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1, minHeight: 32 }}
            data-testid="qa-live-bar"
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: !liveOn ? 'text.disabled' : liveError ? 'error.main' : 'success.main',
                animation: liveOn && !liveError ? 'qaPulse 2s infinite' : 'none',
                '@keyframes qaPulse': { '50%': { opacity: 0.3 } },
              }}
            />
            <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {!liveOn
                ? '자동 갱신 꺼짐'
                : liveError
                ? `자동 갱신 실패 — ${liveError}`
                : liveAge === null
                ? '현장 상태 읽는 중…'
                : `${liveMs / 1000}초마다 자동 갱신 · ${liveAge}초 전`}
              {live?.lightsError
                ? ` · ${live.lightsError}`
                : live && Object.keys(live.lights).length === 0
                ? ` · ${NO_LIGHTS}`
                : ''}
            </Typography>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  size="small"
                  checked={liveOn}
                  onChange={(e) => setLiveOn(e.target.checked)}
                  inputProps={{ 'aria-label': '자동 갱신' }}
                />
              }
              label={<Typography variant="caption">자동</Typography>}
            />
          </Stack>
          {lightSummary && (tab === 'aisle' || tab === 'light') && (
            <Stack
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1 }}
              data-testid="qa-light-summary"
            >
              {tab === 'aisle' && lightSummary.aisleText && (
                <Typography variant="caption" fontWeight={700}>
                  <Box component="span" sx={{ color: LIGHT_COLOR }}>
                    ■
                  </Box>{' '}
                  통로등 {lightSummary.aisleText}
                </Typography>
              )}
              {tab === 'light' && lightSummary.parkOn + lightSummary.parkOff > 0 && (
                <Typography variant="caption" fontWeight={700}>
                  <Box component="span" sx={{ color: SPACE_COLOR.empty }}>
                    ■
                  </Box>{' '}
                  만공차등 켜짐 {lightSummary.parkOn} · 꺼짐 {lightSummary.parkOff}
                </Typography>
              )}
            </Stack>
          )}
          {inspect && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1 }}
              data-testid="qa-inspect-legend"
            >
              <Legend color={INSPECT_COLOR.error} label={`문제 ${inspect.counts.error}`} round />
              <Legend color={INSPECT_COLOR.warning} label={`확인 필요 ${inspect.counts.warning}`} round />
              <Legend color={NEUTRAL_COLOR} label="나머지는 회색" faded round />
            </Stack>
          )}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            {(
              [
                ['name', 'CCTV 이름'],
                ['ip', 'CCTV IP'],
              ] as const
            ).map(([key, label]) => (
              <FormControlLabel
                key={key}
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={labels[key]}
                    onChange={(e) => toggleLabel(key, e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                }
                label={<Typography variant="caption">{label}</Typography>}
              />
            ))}
            <Legend color={BELL_COLOR} label="비상벨" round ring />
          </Stack>
          <QaSiteMap
            key={`${ip}-${activeLevel}`}
            showCctvName={labels.name}
            showCctvIp={labels.ip}
            mapData={activeMap}
            spaces={showSpaces ? levelSpaces : undefined}
            heldCctvs={inspect ? undefined : heldCctvs}
            pending={pendingNames}
            onSpaceTap={showSpaces ? tapSpace : undefined}
            onCameraTap={setOpenCctv}
            lights={!inspect && live && !live.lightsError ? live.lights : undefined}
            camera={showSpaces ? cameraBySpace : undefined}
            cameraHealth={inspect ? undefined : cameraHealth}
            markedCctvs={tab === 'aisle' ? aisleReacted : undefined}
            inspect={inspect}
            aisleManual={live?.lightMode?.aisle.mode === 'manual'}
            parkingManual={live?.lightMode?.parking.mode === 'manual'}
          />
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            {tab === 'aisle'
              ? '켜진 통로등만 밝기 그대로 노랗게 칠합니다(30% 면 30%, 확대하면 %). 꺼진 통로등은 비워 둡니다. CCTV 를 누르면 그 CCTV 의 조명과 주차면을 봅니다.'
              : inspect
                ? '문제 있는 CCTV·비상벨·통로등만 칠합니다 — 빨강은 문제, 주황은 확인 필요, 나머지는 회색입니다. CCTV 를 누르면 상세를 봅니다.'
                : '주차면을 누르면 점유 on/off, CCTV 를 누르면 연결 주차면·ROI·카메라 판정을 보고 점유를 바꿀 수 있습니다. 노란 점선은 카메라 판정이 DB 와 다른 면입니다.'}
            {tab === 'light' && ' CCTV 옆 네모는 만공차등(초록 켜짐·회색 꺼짐)입니다.'}
          </Typography>
        </Box>
      )}

      {ip && (tab === 'aisle' || tab === 'light') && lightLocation && (
        <QaLightControlPanel
          key={`${ip}-${lightLocation}-${tab}`}
          projectId={projectId}
          ip={ip}
          location={lightLocation}
          kind={tab === 'aisle' ? 'aisle' : 'parking'}
        />
      )}

      {ip && tab === 'aisle' && (
        <QaAislePanel
          watch={aisle}
          unavailable={
            live?.lightsError ??
            (live && Object.keys(live.lights).length === 0 ? NO_LIGHTS : undefined)
          }
          onReset={() => setAisle(EMPTY_AISLE_WATCH)}
        />
      )}

      {ip && tab === 'ping' && (
        <QaPingPanel projectId={projectId} ip={ip} onOpen={setOpenCctv} onResult={setPingResult} />
      )}

      {ip && tab === 'module' && (
        <QaModulePanel projectId={projectId} ip={ip} onOpen={setOpenCctv} onResult={setModuleResult} />
      )}

      {ip && tab === 'pairing' && (
        <QaPairingPanel projectId={projectId} ip={ip} onOpen={setOpenCctv} onResult={setPairingResult} />
      )}

      {ip && tab === 'light' && cctvs !== null && (
        <QaOccupancyLightPanel
          cctvs={visible}
          pending={pending}
          lights={live && !live.lightsError ? live.lights : undefined}
          query={query}
          onQuery={setQuery}
          onToggle={(sp) => apply([sp.id], sp.status === 'Y' ? 'N' : 'Y')}
          onApply={(ids, status) => apply(ids, status)}
        />
      )}

      {ip && qaState && (tab === 'board' || tab === 'entrance') && (
        <QaScenarioPanel
          mode={tab}
          level={activeLevel}
          spaces={qaState.spaces}
          boards={qaState.boards}
          pending={pending}
          onApply={applyPlan}
        />
      )}

      {ip && (
        <QaCctvDialog
          projectId={projectId}
          ip={ip}
          name={openCctv}
          spaces={(qaState?.spaces ?? []).filter((s) => s.cctv === openCctv)}
          held={!!openCctv && heldCctvs.has(openCctv)}
          pending={pending}
          onToggle={(s) => apply([s.id], s.status === 'Y' ? 'N' : 'Y')}
          onApplyAll={(ids, status) => apply(ids, status)}
          onClose={() => setOpenCctv(null)}
          liveDetection={openCctv ? live?.detection[openCctv] : undefined}
          liveNow={live?.now}
          lights={openCctv && live && !live.lightsError ? live.lights[openCctv] : undefined}
        />
      )}

      {ip && cctvs === null && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {ip && cctvs !== null && cctvs.length === 0 && tab === 'light' && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          현장 router 에 등록된 CCTV 가 없습니다. 맵을 먼저 등록하세요.
        </Typography>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.severity === 'error' ? 6000 : 3000}
        onClose={(_, reason) => reason !== 'clickaway' && setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 16, sm: 24 } }}
      >
        <Alert
          severity={toast?.severity ?? 'info'}
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ width: '100%', maxWidth: 560 }}
        >
          {toast?.text}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

/** 맵 위 범례 한 칸. */
const Legend: React.FC<{
  color: string;
  label: string;
  faded?: boolean;
  dashed?: boolean;
  round?: boolean;
  ring?: boolean;
}> = ({ color, label, faded, dashed, round, ring }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Box
      sx={{
        width: 12,
        height: 12,
        bgcolor: dashed || ring ? 'transparent' : color,
        border: dashed ? `2px dashed ${color}` : ring ? `2.5px solid ${color}` : undefined,
        borderRadius: round ? '50%' : 0.5,
        opacity: faded ? 0.35 : 0.85,
        flexShrink: 0,
      }}
    />
    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
      {label}
    </Typography>
  </Stack>
);

export default ParkingQaSection;
